import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { portalPath } from '../config/portalPaths';
import { useAuthStore, hasAdminPermission } from '../stores/authStore';
import { useCustomerAuthStore } from '../stores/customerAuthStore';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  requireCustomerAuth?: boolean;
  allowRecovery?: boolean;
}

export default function ProtectedRoute({ children, allowedRoles, requireCustomerAuth, allowRecovery }: ProtectedRouteProps) {
  const { isAuthenticated, user } = useAuthStore();
  const { isAuthenticated: isCustomerAuth, isInitialized, initialize } = useCustomerAuthStore();

  useEffect(() => {
    if (requireCustomerAuth && !isInitialized) initialize();
  }, [requireCustomerAuth, isInitialized, initialize]);

  if (requireCustomerAuth) {
    if (!isInitialized) return <div className="min-h-screen bg-slate-50" aria-busy="true" />;
    if (!isCustomerAuth) {
      return <Navigate to={portalPath('/login')} replace />;
    }
    return <>{children}</>;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  if (user.recovery_scope && !allowRecovery) return <Navigate to="/maintenance-recovery" replace />;

  if (allowedRoles && allowedRoles.length > 0) {
    const effectiveRoles: string[] = [user.role];
    if (user.isAdmin || user.is_admin) effectiveRoles.push('admin');

    const isAllowedByRole = allowedRoles.some((r) => effectiveRoles.includes(r));
    const isAllowedByPermission = allowedRoles.includes('admin') && hasAdminPermission(user);

    if (!isAllowedByRole && !isAllowedByPermission) {
      return <Navigate to="/login" replace />;
    }
  }

  return <>{children}</>;
}
